'use client';

import type { SearchProductResult } from '@chemist4u/newt-components';
import { CONSENT_UPDATED_EVENT, hasPreferencesConsent } from '@lib/analytics';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

const RECENT_SEARCHES_KEY = '_recent_searches';

type SearchContextType = {
  query: string;
  results: SearchProductResult[];
  setQuery: (query: string) => void;
  isLoading: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  recentSearches: string[];
  setRecentSearches: (searches: string[]) => void;
};

const SearchContext = createContext<SearchContextType | null>(null);

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (context === null) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};

type SearchProviderProps = {
  children?: React.ReactNode;
  query: string;
  results: SearchProductResult[];
  setQuery: (query: string) => void;
  isLoading: boolean;
};

export const SearchProvider = ({
  children,
  query,
  results,
  setQuery,
  isLoading,
}: SearchProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Load recent searches from localStorage when preferences consent is granted
  useEffect(() => {
    try {
      const stored = hasPreferencesConsent()
        ? localStorage.getItem(RECENT_SEARCHES_KEY)
        : null;
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  // Clear persisted recent searches and in-memory state when preferences
  // consent is withdrawn
  useEffect(() => {
    const handleConsentUpdated = () => {
      if (!hasPreferencesConsent()) {
        localStorage.removeItem(RECENT_SEARCHES_KEY);
        setRecentSearches([]);
      }
    };

    window.addEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdated);
    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdated);
    };
  }, []);

  return (
    <SearchContext.Provider
      value={{
        query,
        results,
        setQuery,
        isLoading,
        isOpen,
        setIsOpen,
        recentSearches,
        setRecentSearches,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
